/**
 * NFC Connector Service
 *
 * WebSocket client for the local NFC connector Go app.
 * The connector runs on the user's machine and bridges the NFC reader to the webapp.
 *
 * Protocol:
 *   Incoming (webapp → connector): { id, device_id, action, payload }
 *   Outgoing (connector → webapp): { id, event, status, payload }
 *
 * Reading is automatic — the connector broadcasts nfc-card-read when a card
 * is placed on the reader. No command is needed to start listening.
 *
 * Writing: send nfc-write with { credential, hash } → response with same ID.
 *
 * Reader contention (bridge 1.0.15+): when another PC/SC client holds the
 * reader, taps produce nothing at all — no read, no error. The bridge detects
 * it and broadcasts nfc-reader-busy / nfc-reader-available, which this service
 * turns into `isReaderContended` + a toast so the reader never looks ready
 * while every tap is being dropped.
 */

import { authService } from '@/core/auth/authService';
import { apiClient } from './api';
import { notifyInfo, notifyWarning, notifyError } from './notificationService';
import type { ApiError } from '@/types/global';
import i18n from '@/core/i18n/i18n';

const CONNECTOR_HEALTH_URL = 'https://localhost:12212/health';
const CONNECTOR_DIAGNOSTICS_URL = 'https://localhost:12212/diagnostics';

/**
 * Minimum time between two user-initiated connection attempts (hover/click on
 * the NFC icon, opening a wristband dialog, etc). Prevents a stream of hover
 * events from bursting requests when bridge is down.
 */
const MANUAL_ATTEMPT_COOLDOWN_MS = 3_000;

/**
 * Sentinel thrown when the server reports the NFC connector feature is
 * unavailable (503 + { available: false }). Callers can distinguish this from
 * genuine network/auth errors and suppress noisy reconnect attempts.
 */
export class NFCConnectorDisabledError extends Error {
  reason: string;
  constructor(reason: string) {
    super(`NFC connector disabled on server: ${reason}`);
    this.name = 'NFCConnectorDisabledError';
    this.reason = reason;
  }
}

function isApiError(e: unknown): e is ApiError {
  return typeof e === 'object' && e !== null && 'status' in e && 'code' in e;
}

function readDisabledReason(err: unknown): string | null {
  if (!isApiError(err)) return null;
  if (err.status !== 503) return null;
  const body = err.data as { available?: boolean; reason?: string } | undefined;
  if (body && body.available === false) {
    return body.reason || 'unavailable';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NfcDeviceInfo {
  id: string;
  name: string;
  type: string;
  status: string;
  /** Raw hardware identifier — used to match device-disconnected events. */
  hardwareId?: string;
}

export interface NfcCardReadPayload {
  deviceId: string;
  uid: string;
  credential?: string;
  ndef: string;
  chipType: string;
  chipName: string;
}

type NfcEventType =
  | 'status-changed'
  | 'busy-changed'
  | 'connected'
  | 'disconnected'
  | 'terminal-ready'
  | 'nfc-card-detected'
  | 'nfc-card-read'
  | 'nfc-card-removed'
  | 'nfc-reader-busy'
  | 'nfc-reader-available'
  | 'nfc-read-error'
  | 'device-discovered'
  | 'device-disconnected';

type EventHandler<T = Record<string, unknown>> = (payload: T) => void;

interface WsMessage {
  id: string;
  event: string;
  status: 'ok' | 'error';
  payload: Record<string, unknown>;
}

interface ConnectorSessionResponse {
  token: string;
  expiresIn: number;
  wsUrl: string;
}

// ---------------------------------------------------------------------------
// Service class (singleton)
// ---------------------------------------------------------------------------

class NfcConnectorService {
  private ws: WebSocket | null = null;
  private _status: 'disconnected' | 'connecting' | 'connected' = 'disconnected';
  private _devices: NfcDeviceInfo[] = [];
  private _busy = false;
  /**
   * True while the bridge reports that ANOTHER application holds the NFC
   * reader (bridge event `nfc-reader-busy`). Taps produce nothing at all in
   * that state — not even an error — so without this the reader looks ready
   * and every scan silently does nothing. Distinct from `_busy`, which means
   * *this* tab is running a sync.
   */
  private _readerContended = false;
  private connectPromise: Promise<void> | null = null;
  private lastAttemptAt = 0;

  private listeners = new Map<string, Set<EventHandler>>();
  // Pending request promises: requestId → { resolve, reject }
  private pending = new Map<string, { resolve: (m: WsMessage) => void; reject: (e: Error) => void }>();

  // ---------------------------------------------------------------------------
  // Connection
  // ---------------------------------------------------------------------------

  private setStatus(next: 'disconnected' | 'connecting' | 'connected'): void {
    if (this._status === next) return;
    this._status = next;
    this.emit('status-changed', { status: next });
  }

  private async requestConnectorSession(endpoint: string): Promise<string> {
    const response = await apiClient.post<ConnectorSessionResponse>(
      endpoint,
      { origin: window.location.origin },
      { showErrorToast: false, skipAuthRedirect: true },
    );
    if (!response.data.wsUrl) {
      throw new Error('Missing wsUrl');
    }
    return response.data.wsUrl;
  }

  private async getConnectorWsUrl(): Promise<string> {
    // Try authenticated session first
    try {
      return await this.requestConnectorSession('/api/v1/pos/nfc-connector/session/');
    } catch (err) {
      // If the server reports the feature is disabled, don't try any fallback
      // (credential session would also return 503). Bubble up the sentinel so
      // callers can install a long backoff instead of hammering the server.
      const reason = readDisabledReason(err);
      if (reason) {
        throw new NFCConnectorDisabledError(reason);
      }
      try {
        const refreshed = await authService.refreshSession();
        if (refreshed) {
          return await this.requestConnectorSession('/api/v1/pos/nfc-connector/session/');
        }
      } catch {
        // fall through to public credential-login session
      }
    }

    try {
      return await this.requestConnectorSession('/api/v1/auth/credential-nfc-session/');
    } catch (err) {
      const reason = readDisabledReason(err);
      if (reason) {
        throw new NFCConnectorDisabledError(reason);
      }
      throw new Error(
        i18n.t('nfc.connector.sessionError', 'Could not establish a secure session with the NFC connector. Check that it is installed and running with permissions.'),
      );
    }
  }

  private async buildConnectionFailureMessage(): Promise<string> {
    try {
      const response = await fetch(CONNECTOR_HEALTH_URL, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-store',
      });
      if (response.ok) {
        return i18n.t('nfc.connector.wsOpenFailed', 'Shaka Bridge is responding, but the browser could not open the secure WebSocket. Restart Chrome/Brave/Safari and try {{url}}', { url: CONNECTOR_DIAGNOSTICS_URL });
      }
    } catch {
      // If health fails too, we keep the more general guidance below.
    }
    return i18n.t('nfc.connector.connectionFailed', 'Could not connect to Shaka Bridge. Check that it is open and, if you just installed or repaired the certificate, restart the browser. Diagnostics: {{url}}', { url: CONNECTOR_DIAGNOSTICS_URL });
  }

  /**
   * Attempt to establish a connection to the local bridge.
   *
   * On-demand model: there is NO automatic retry loop. Callers invoke this:
   *   (a) once at app mount to try an initial connection,
   *   (b) when a feature that needs NFC is activated (e.g. WristbandSyncDialog),
   *   (c) when the user hovers / clicks the NFC icon in the header.
   *
   * Concurrency: if a connection attempt is already in flight, the same
   * `connectPromise` is returned — a second caller will not fire a second
   * request.
   *
   * If already connected, resolves immediately without doing anything.
   */
  connect(): Promise<void> {
    if (this._status === 'connected') return Promise.resolve();
    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.lastAttemptAt = Date.now();
    this.setStatus('connecting');
    this.connectPromise = (async () => {
      let wsUrl: string;
      try {
        wsUrl = await this.getConnectorWsUrl();
      } catch (error) {
        this.setStatus('disconnected');
        this.connectPromise = null;
        throw error;
      }

      return new Promise<void>((resolve, reject) => {
        let settled = false;
        const fail = (error: Error) => {
          if (settled) return;
          this.setStatus('disconnected');
          this.connectPromise = null;
          settled = true;
          reject(error);
        };

        try {
          this.ws = new WebSocket(wsUrl);
        } catch (e) {
          fail(new Error(i18n.t('nfc.connector.wsCreateFailed', 'Could not create the secure WebSocket connection. Diagnostics: {{url}}', { url: CONNECTOR_DIAGNOSTICS_URL })));
          return;
        }

        const timeout = setTimeout(() => {
          this.ws?.close();
          fail(new Error(i18n.t('nfc.connector.timeout', 'Timeout connecting to the NFC connector. Check {{url}}', { url: CONNECTOR_DIAGNOSTICS_URL })));
        }, 5000);

        this.ws.onopen = () => {
          clearTimeout(timeout);
          settled = true;
          this.setStatus('connected');
          this.connectPromise = null;
          this.emit('connected', {});
          resolve();
        };

        this.ws.onclose = () => {
          clearTimeout(timeout);
          this.setStatus('disconnected');
          this.connectPromise = null;
          this._devices = [];
          // Contention is unknowable with the bridge gone; drop it silently
          // (no "available again" toast) so a reconnect starts from a clean
          // state instead of inheriting a stale "reader in use".
          this._readerContended = false;
          for (const { reject: rej } of this.pending.values()) {
            rej(new Error(i18n.t('nfc.connector.wsDisconnected', 'WebSocket disconnected')));
          }
          this.pending.clear();
          if (!settled) {
            settled = true;
            reject(new Error('Connection failed'));
          }
          this.emit('disconnected', {});
        };

        this.ws.onerror = () => {
          clearTimeout(timeout);
          void (async () => {
            fail(new Error(await this.buildConnectionFailureMessage()));
          })();
        };

        this.ws.onmessage = (evt) => {
          try {
            const msg: WsMessage = JSON.parse(evt.data as string);
            this.handleMessage(msg);
          } catch {
            // ignore malformed
          }
        };
      });
    })();

    return this.connectPromise;
  }

  disconnect(): void {
    this.ws?.close();
    this.ws = null;
    this.setStatus('disconnected');
  }

  // ---------------------------------------------------------------------------
  // Commands
  // ---------------------------------------------------------------------------

  /**
   * Send a command to the connector and wait for the matching response (by ID).
   * Resolves with the response message. Rejects on error response or timeout.
   */
  send(
    action: string,
    deviceId: string,
    payload: Record<string, unknown> = {},
    timeoutMs = 10_000,
  ): Promise<WsMessage> {
    if (this._status !== 'connected' || !this.ws) {
      return Promise.reject(new Error(i18n.t('nfc.connector.notConnected', 'Not connected to the NFC connector')));
    }

    const id = crypto.randomUUID();
    const msg = { id, device_id: deviceId, action, payload };

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(i18n.t('nfc.connector.responseTimeout', 'Timeout waiting for response to {{action}}', { action })));
      }, timeoutMs);

      this.pending.set(id, {
        resolve: (m) => { clearTimeout(timer); resolve(m); },
        reject: (e) => { clearTimeout(timer); reject(e); },
      });

      this.ws!.send(JSON.stringify(msg));
    });
  }

  /**
   * Write a logical wristband credential to the card on the given device.
   * Rejects on error or timeout.
   */
  async writeCard(deviceId: string, credential: string, hash?: string): Promise<void> {
    const res = await this.send('nfc-write', deviceId, {
      credential,
      uuid: credential,
      hash: hash ?? '',
    });
    if (res.status === 'error') {
      throw new Error((res.payload.message as string) ?? i18n.t('nfc.connector.writeError', 'Error writing to the wristband'));
    }
  }

  // ---------------------------------------------------------------------------
  // Event listeners
  // ---------------------------------------------------------------------------

  on<T = Record<string, unknown>>(event: NfcEventType | string, handler: EventHandler<T>): void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(handler as EventHandler);
  }

  off<T = Record<string, unknown>>(event: NfcEventType | string, handler: EventHandler<T>): void {
    this.listeners.get(event)?.delete(handler as EventHandler);
  }

  /** Listen for an event once. Returns a cleanup function. */
  once<T = Record<string, unknown>>(event: NfcEventType | string, handler: EventHandler<T>): () => void {
    const wrapper: EventHandler = (payload) => {
      this.off(event, wrapper);
      (handler as EventHandler)(payload);
    };
    this.on(event, wrapper);
    return () => this.off(event, wrapper);
  }

  /** Wait for a specific event. Rejects after timeoutMs. */
  waitFor<T = Record<string, unknown>>(event: NfcEventType | string, timeoutMs = 60_000): Promise<T> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.off(event, handler);
        reject(new Error(i18n.t('nfc.connector.eventTimeout', 'Timeout waiting for event {{event}}', { event })));
      }, timeoutMs);

      const handler: EventHandler = (payload) => {
        clearTimeout(timer);
        resolve(payload as T);
      };
      this.once(event, handler);
    });
  }

  // ---------------------------------------------------------------------------
  // Accessors
  // ---------------------------------------------------------------------------

  get status() { return this._status; }
  get devices() { return this._devices; }
  get isBusy() { return this._busy; }
  /** Another application holds the reader — taps will not be seen until it lets go. */
  get isReaderContended() { return this._readerContended; }
  get firstNfcDevice(): NfcDeviceInfo | null {
    return this._devices.find(d => d.type === 'nfc') ?? this._devices[0] ?? null;
  }

  /**
   * User-initiated connection attempt (icon hover/click, opening an NFC
   * feature). Silently short-circuits if:
   *   - already connected
   *   - a connection attempt is already in flight (dedupe via connectPromise)
   *   - the previous attempt was started less than MANUAL_ATTEMPT_COOLDOWN_MS
   *     ago (prevents hover event bursts from bursting requests)
   *
   * Errors are swallowed — the caller typically doesn't care; the icon state
   * will reflect the outcome via status-changed events.
   */
  tryConnectFromUi(): Promise<void> {
    if (this._status === 'connected') return Promise.resolve();
    if (this.connectPromise) return this.connectPromise;
    const elapsed = Date.now() - this.lastAttemptAt;
    if (elapsed < MANUAL_ATTEMPT_COOLDOWN_MS) return Promise.resolve();
    return this.connect().catch(() => {
      // Swallow — icon state already reflects disconnected via events.
    });
  }

  private bootAttempted = false;

  /**
   * One silent connection attempt per page load (app open / refresh), so
   * the indicator goes green without hovering when the bridge is running.
   * Strictly single-shot: a user without the local bridge sees exactly one
   * browser-logged connection error per load (the browser logs WebSocket
   * refusals natively; JS cannot suppress them) — never a polling loop.
   * Subsequent attempts stay user-triggered (hover/click on the icon).
   */
  tryConnectOnBoot(): void {
    if (this.bootAttempted) return;
    this.bootAttempted = true;
    void this.tryConnectFromUi();
  }

  /**
   * Track the reader-contention state reported by the bridge and make it
   * visible. A toast fires only on the transition — the bridge emits
   * `nfc-reader-busy` once when contention starts and `nfc-reader-available`
   * once when it clears, and the indicator carries the state in between, so
   * nothing repeats while the other app keeps holding the reader.
   */
  private setReaderContended(next: boolean): void {
    if (this._readerContended === next) return;
    this._readerContended = next;
    if (next) {
      notifyWarning(i18n.t('nfc.readerBusy.title', 'NFC reader in use by another application'), {
        description: i18n.t(
          'nfc.readerBusy.description',
          'Taps are not reaching Shaka. Close the other application using the reader.',
        ),
        duration: 8000,
      });
    } else {
      notifyInfo(i18n.t('nfc.readerBusy.cleared', 'NFC reader available again'));
    }
  }

  /** Signal that the connector is actively being used (e.g. wristband sync in progress). */
  setBusy(busy: boolean): void {
    if (this._busy === busy) return;
    this._busy = busy;
    this.emit('busy-changed', { busy });
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  /**
   * Should THIS tab act on reader broadcasts? The bridge broadcasts card
   * events to every connected client — with several deck windows open
   * (even prod + staging side by side) a single tap used to fire the scan
   * popover / sync flow in ALL of them. Only the tab the operator is
   * actually using may react:
   *   - hidden tab/window            → never.
   *   - visible + focused            → yes.
   *   - visible + unfocused          → only while THIS tab runs an explicit
   *     sync flow (busy) — starting the wristband dialog is clear intent,
   *     and focus may legitimately sit on another app while tapping.
   */
  private shouldReceiveCardEvents(): boolean {
    if (typeof document === 'undefined') return true;
    if (document.visibilityState !== 'visible') return false;
    return document.hasFocus() || this._busy;
  }

  private handleMessage(msg: WsMessage): void {
    // 1. Route to pending request handler if ID matches
    const pending = this.pending.get(msg.id);
    if (pending) {
      this.pending.delete(msg.id);
      if (msg.status === 'error') {
        pending.reject(new Error((msg.payload.message as string) ?? i18n.t('nfc.connector.eventError', 'Error in {{event}}', { event: msg.event })));
      } else {
        pending.resolve(msg);
      }
      return;
    }

    // Card broadcasts are gated per-tab — see shouldReceiveCardEvents().
    // Device/status events still flow everywhere (they carry no user intent).
    if (
      (msg.event === 'nfc-card-detected'
        || msg.event === 'nfc-card-read'
        || msg.event === 'nfc-card-removed'
        || msg.event === 'nfc-read-error')
      && !this.shouldReceiveCardEvents()
    ) {
      return;
    }

    // 2. Handle broadcast events
    switch (msg.event) {
      case 'terminal-ready': {
        const rawDevices = (msg.payload.devices as Array<Record<string, unknown>>) ?? [];
        this._devices = rawDevices.map(d => ({
          id: d.id as string,
          name: d.name as string,
          type: d.type as string,
          status: d.status as string,
          hardwareId: (d.hardware_id as string) ?? undefined,
        }));
        this.emit('terminal-ready', { devices: this._devices });
        break;
      }
      case 'nfc-card-detected':
        this.emit('nfc-card-detected', msg.payload);
        break;
      case 'nfc-card-read':
        this.emit('nfc-card-read', {
          deviceId: msg.payload.device_id,
          uid: msg.payload.uid,
          credential: (msg.payload.credential as string | undefined) ?? undefined,
          ndef: msg.payload.ndef,
          chipType: msg.payload.chip_type,
          chipName: msg.payload.chip_name,
        });
        break;
      case 'nfc-card-removed':
        this.emit('nfc-card-removed', msg.payload);
        break;
      // Reader contention. NOT gated per-tab like card events: this is reader
      // state, not a scan meant for one operator, and every open tab shows a
      // reader that cannot read. Payload: { device_id, code, reason }.
      case 'nfc-reader-busy':
        this.setReaderContended(true);
        this.emit('nfc-reader-busy', msg.payload);
        break;
      case 'nfc-reader-available':
        this.setReaderContended(false);
        this.emit('nfc-reader-available', msg.payload);
        break;
      case 'nfc-read-error': {
        // A tag was detected but the read failed. Surface it visibly so it's
        // never silent, and keep it non-fatal (the reader stays connected).
        // `code` is a stable, language-agnostic key from the bridge.
        const code = (msg.payload.code as string | undefined) ?? 'read_failed';
        this.emit('nfc-read-error', {
          deviceId: msg.payload.device_id,
          code,
          reason: msg.payload.reason,
        });
        if (code === 'reader_busy') {
          // Transient contention (usually the OS smart-card service). Retryable.
          notifyWarning(i18n.t(
            'nfc.readError.readerBusy',
            'The reader was busy — please tap the wristband again.',
          ));
        } else {
          notifyError(i18n.t(
            'nfc.readError.readFailed',
            'Could not read the wristband. Please tap it again.',
          ));
        }
        break;
      }
      case 'device-discovered': {
        // Add the newly discovered device to the local list so firstNfcDevice updates.
        // Payload: { hardware_id, hardware_name, type, transport }
        const hwId = msg.payload.hardware_id as string;
        const already = this._devices.some(d => d.hardwareId === hwId || d.id === hwId);
        if (!already) {
          this._devices = [
            ...this._devices,
            {
              id: hwId,
              name: (msg.payload.hardware_name as string) ?? hwId,
              type: (msg.payload.type as string) ?? 'nfc',
              status: 'connected',
              hardwareId: hwId,
            },
          ];
        }
        this.emit('device-discovered', msg.payload);
        break;
      }
      case 'device-disconnected': {
        // Payload: { hardware_id }
        const removedHwId = msg.payload.hardware_id as string | undefined;
        if (removedHwId) {
          this._devices = this._devices.filter(
            d => d.hardwareId !== removedHwId && d.id !== removedHwId,
          );
        }
        this.emit('device-disconnected', msg.payload);
        break;
      }
    }
  }

  private emit(event: string, payload: Record<string, unknown>): void {
    const handlers = this.listeners.get(event);
    if (!handlers) return;
    for (const handler of handlers) {
      try { handler(payload); } catch { /* ignore */ }
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Singleton
// ─────────────────────────────────────────────────────────────────────────

const nfcConnector = new NfcConnectorService();

export { nfcConnector };
